import { useMemo } from 'react';
import { Empty } from 'antd';
import {
  Card,
  SkeletonLoader,
  Logo,
  ChatLayout,
} from '@insight-health-ai/roseate/components';
import {
  MessageTextSquare01,
  Download01,
} from '@insight-health-ai/roseate/icons';
import {
  useGetTranscriptionList,
  UseTranscriptionListParams,
} from 'src/hooks/transcription';
import { theme, useTheme } from '@insight-health-ai/roseate/theme';
import { downloadTranscription } from 'src/utils/transcription';
import { NonOptional } from '@insight-health-ai/roseate/utils';
import { Patient } from 'src/models';
import { AudioPlayer, AudioPlayerProps } from './audioPlayer';
import { useGetCurrentPractitioner } from 'src/hooks/settings';

type TranscriptionCardProps = {
  filters: NonOptional<
    Pick<UseTranscriptionListParams, 'encounterId' | 'encounterPhase' | 'type'>,
    'encounterId'
  >;
  patient?: Pick<Patient, 'first_name' | 'last_name'>;
  title?: string;
  audio?: AudioPlayerProps;
};

export const TranscriptionCard: React.FC<TranscriptionCardProps> = ({
  filters,
  patient,
  title,
  audio,
}) => {
  const { colors } = useTheme();

  const { isSuperAdmin } = useGetCurrentPractitioner();

  const { data: transcriptionData, isLoading } = useGetTranscriptionList({
    ...filters,
  });

  const transcriptionJson =
    transcriptionData?.transcriptions[0]?.transcription_json;
  const hasTranscriptionData =
    Array.isArray(transcriptionJson) && transcriptionJson?.length > 0;

  const chatRoles = {
    agent: {
      label: 'Assistant',
      align: 'left',
      avatar: {
        text: <Logo compact className="h-5" />,
        style: {
          backgroundColor: colors.white,
          color: colors.white,
          boxShadow: theme.boxShadow.md,
        },
      },
    },
    user: {
      label: patient
        ? [patient.first_name, patient.last_name].filter(Boolean).join(' ')
        : 'P',
      align: 'right',
      avatar: {
        style: {
          color: colors.white,
          boxShadow: theme.boxShadow.md,
        },
      },
    },
  } as const;

  const chatMessages = useMemo(
    () =>
      (transcriptionJson || []).map((message, index) => {
        const { role, content, timestamp } = message;
        return {
          role: role!,
          content,
          id: String(index),
          timestamp: timestamp ? new Date(timestamp) : undefined,
        };
      }),
    [transcriptionJson]
  );

  const renderTranscription = () => {
    if (!hasTranscriptionData) {
      return (
        <div className="flex justify-center p-4">
          <Empty className="max-w-80" description="No transcript available" />
        </div>
      );
    }

    return (
      <ChatLayout
        messages={chatMessages}
        roles={chatRoles}
        avatar={{
          align: 'top',
          size: 'md',
        }}
      />
    );
  };
  return (
    <Card
      outlined={false}
      className="flex-1 min-w-[320px] h-full overflow-hidden"
      padding={{ body: 'none', header: 'sm' }}
      title={
        <div className="flex items-center gap-2">
          <MessageTextSquare01 className="text-primary" />
          {title || 'Transcript'}
        </div>
      }
      actions={{
        list:
          hasTranscriptionData && isSuperAdmin
            ? [
                {
                  label: 'Download',
                  icon: <Download01 />,
                  onClick: () =>
                    downloadTranscription(transcriptionJson, {
                      roles: chatRoles,
                      filename: patient
                        ? `${patient.first_name}-${patient.last_name}-transcription.txt`
                        : `transcription.txt`,
                    }),
                  variant: 'outlined',
                },
              ]
            : [],
      }}
    >
      <div className="flex flex-col gap-2">
        {audio?.src && (
          <div className="w-full sticky top-0 z-10">
            <AudioPlayer showSkipControls={false} src={audio.src} />
          </div>
        )}

        {isLoading ? (
          <div className="p-4">
            <SkeletonLoader active />
          </div>
        ) : (
          renderTranscription()
        )}
      </div>
    </Card>
  );
};
